// API Service for PHP Backend Integration
import environment from "../config/environment";
import BaseApiService from "./BaseApiService";

class ApiService extends BaseApiService {
  constructor() {
    super(); // Initialize BaseApiService with default PHP API URL and timeout
  }

  // Login method that integrates with LoginEduokCrm.php
  async login(userId, password) {
    try {
      // Login is handled by LoginEduokCrm.php under TARGET_DIR_CRM
      const systemCode = environment.SYSTEM_CODE;
      const applicationId = "";

      const params = new URLSearchParams({
        user_id: userId,
        password: password,
        system_code: systemCode,
        application_id: applicationId,
      });

      const loginUrl = `${environment.SERVER_NAME}${environment.TARGET_DIR_CRM}LoginEduokCrm.php?${params.toString()}`;

      // Always log the full login URL
      console.log("[ApiService] Login API Full URL:", loginUrl);
      
      if (environment.DEBUG_MODE) {
        // Helpful in development to verify the exact URL being called
        // Note: password is present in query for this legacy endpoint; avoid logging credentials in production
        console.log("[ApiService] Login URL (DEBUG):", loginUrl);
      }

      // Call directly to allow different base path than PHP_API_URL
      const response = await this.makeRequest(loginUrl);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: {
            staff: response.staff,
            raw: response,
          },
          message: "Login successful",
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Login failed",
          message: response.api_remark || "Login failed",
        };
      }
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Get Admin Dashboard Data
  async getCrmAdminDashboard() {
    try {
      const dashboardUrl = `${environment.SERVER_NAME}${environment.TARGET_DIR_CRM}getCrmAdminDashboard.php`;
      
      console.log("[ApiService] Admin Dashboard API Full URL:", dashboardUrl);
      
      const response = await this.makeRequest(dashboardUrl);

      return this.handleResponse(response, "Dashboard data retrieved successfully", "Failed to fetch dashboard data");
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Method to get user profile data
  async getUserProfile(userId) {
    try {
      const response = await this.makeRequest(
        `GetUserProfile.php?user_id=${userId}`
      );

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get user profile",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to update user profile
  async updateUserProfile(userId, profileData) {
    try {
      const response = await this.makeRequest("UpdateUserProfile.php", {
        method: "POST",
        body: JSON.stringify({
          user_id: userId,
          ...profileData,
        }),
      });

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to update user profile",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get school data
  async getSchoolData() {
    try {
      const response = await this.makeRequest("GetSchoolData.php");

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get school data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get students data
  async getStudents(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetStudents.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get students data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get teachers data
  async getTeachers(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetTeachers.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get teachers data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get classes data
  async getClasses(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetClasses.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get classes data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get attendance data
  async getAttendance(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetAttendance.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get attendance data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get fees data
  async getFees(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetFees.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get fees data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get grades data
  async getGrades(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetGrades.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get grades data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get reports data
  async getReports(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetReports.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get reports data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Get Financial Year List
  async getFinancialYearList() {
    try {
      const endpoint = "getFinancialYearList.php";
      const fullUrl = `${this.baseURL}/${endpoint}`;

      console.log("[ApiService] getFinancialYearList API Call:");
      console.log("  Full URL:", fullUrl);

      const response = await this.makeRequest(endpoint);
      console.log("[ApiService] getFinancialYearList Response:", response);

      // Try to normalize different possible response shapes
      const years =
        response.data ||
        response.financial_years ||
        response.years ||
        response.list ||
        [];

      if (response.api_status === "OK") {
        return {
          success: true,
          data: years,
          message: response.api_remark || "Financial year list retrieved successfully",
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get financial year list",
          message: response.api_remark || "Failed to get financial year list",
        };
      }
    } catch (error) {
      console.error("[ApiService] getFinancialYearList Error:", error);
      return {
        success: false,
        error: error.message,
        message: "Network error or server unavailable",
      };
    }
  }

  // Get My Schools - Filtered by financial year and month
  async getMySchools(financialYearId, selectedMonth = null, limit = 100, offset = 0, order = 'ASC') {
    try {
      const params = this.createSearchParams({
        financial_year_id: financialYearId,
        selected_month: selectedMonth,
        limit: limit,
        offset: offset,
        order: order,
      });

      const queryString = params.toString();
      // Use new optimized PHP endpoint with same parameters
      const endpoint = `getMySchoolsNew.php?${queryString}`;
      const fullUrl = `${this.baseURL}/${endpoint}`;
      
      // Log full URL with all parameters
      console.log("[ApiService] getMySchools API Call:");
      console.log("  Full URL:", fullUrl);
      console.log("  Parameters:", {
        financial_year_id: financialYearId,
        selected_month: selectedMonth,
        limit: limit,
        offset: offset,
        order: order,
      });
      console.log("  Query String:", queryString);

      const response = await this.makeRequest(endpoint);
      console.log("[ApiService] getMySchools Response:", response);
      
      return this.handleResponse(response, 'Schools retrieved successfully', 'Failed to retrieve schools');
    } catch (error) {
      console.error("[ApiService] getMySchools Error:", error);
      return this.handleError(error);
    }
  }


  // School Account Management API Methods
  async createSchoolAccount(accountData) {
    try {
      const response = await this.makeRequest('school-accounts/create.php', {
        method: 'POST',
        body: JSON.stringify(accountData),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Account created successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to create account',
          message: response.api_remark || 'Failed to create account',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  async getSchoolAccounts(schoolId = null) {
    try {
      const params = new URLSearchParams();
      if (schoolId) params.append('school_id', schoolId);

      const response = await this.makeRequest(`school-accounts/list.php?${params.toString()}`);

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || response.accounts || [],
          message: 'Accounts retrieved successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to retrieve accounts',
          message: response.api_remark || 'Failed to retrieve accounts',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  async updateSchoolAccountTheme(accountId, themeName) {
    try {
      const response = await this.makeRequest(`school-accounts/update-theme.php?id=${accountId}`, {
        method: 'POST',
        body: JSON.stringify({ theme: themeName }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Theme updated successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to update theme',
          message: response.api_remark || 'Failed to update theme',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
